import {
  HttpClient,
  HttpHeaders,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { Inject, Injectable, Optional } from "@angular/core";
import {
  ApiException,
  API_BASE_URL,
} from "@shared/service-proxies/service-proxies";
import {
  mergeMap as _observableMergeMap,
  catchError as _observableCatch,
  finalize,
  catchError,
  tap,
} from "rxjs/operators";
import {
  Observable,
  throwError as _observableThrow,
  of as _observableOf,
  ReplaySubject,
  BehaviorSubject,
} from "rxjs";

import { FileParameter } from "@shared/components/image-upload/file-parameter";

@Injectable()
export class FileUploadService {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  private _isUploading = new BehaviorSubject<boolean>(false);
  isReloadingData = new BehaviorSubject<boolean>(false);
  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  /**
   * @param files (optional)
   * @return Success
   */
  UploadImages(files: FileParameter[] | undefined): Observable<string[]> {
    let url_ = this.baseUrl + "/api/File/UploadImages";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = new FormData();
    if (files === null || files === undefined)
      throw new Error("The parameter 'files' cannot be null.");
    else
      files.forEach((file) => {
        content_.append("files", file.data, file.fileName);
      });

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUploadFiles(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUploadFiles(<any>response_);
            } catch (e) {
              return <Observable<string[]>>(<any>_observableThrow(e));
            }
          } else
            return <Observable<string[]>>(<any>_observableThrow(response_));
        })
      );
  }

  protected processUploadFiles(
    response: HttpResponseBase
  ): Observable<string[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
        ? (<any>response).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200.push(item);
          } else {
            result200 = <any>null;
          }
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf<string[]>();
  }

  getIsPending(): Observable<boolean> {
    return this._isUploading.asObservable();
  }

  setIsPending(value: boolean) {
    this._isUploading.next(value);
  }

  uploadFile(filePath: string | undefined, file: FileParameter | undefined): Observable<any> {
    let url = `${this.baseUrl}/api/AwsS3/UploadFile?`;
    if (filePath === null)
      throw new Error("The parameter 'filePath' cannot be null.");
    else if (filePath !== undefined)
      url += "filePath=" + encodeURIComponent("" + filePath) + "&";
    url = url.replace(/[?&]$/, "");

    const content_ = new FormData();
    content_.append("file", file.data, file.fileName ? file.fileName : "file");
    let options : any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
      })
  };
  return this.http
      .request("post", url, options)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUploadFileResponse(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUploadFileResponse(<any>response_);
            } catch (e) {
              return <Observable<any>>(<any>_observableThrow(e));
            }
          } else
            return <Observable<any>>(<any>_observableThrow(response_));
        })
      );
  }

  protected processUploadFileResponse(
    response: HttpResponseBase
  ): Observable<string[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
        ? (<any>response).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText) => {
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);

          return _observableOf(resultData200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf<string[]>();
  }

}

function throwException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any
): Observable<any> {
  if (result !== null && result !== undefined) return _observableThrow(result);
  else
    return _observableThrow(
      new ApiException(message, status, response, headers, null)
    );
}

function blobToText(blob: any): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next("");
      observer.complete();
    } else {
      let reader = new FileReader();
      reader.onload = (event) => {
        observer.next((<any>event.target).result);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}
